import type { E164Number } from 'libphonenumber-js';
import libParse from 'libphonenumber-js';
import ReactPhoneNumberInput from 'react-phone-number-input/input';

import { useField } from '@adflow/frontend/util-common';

import type { FormControlProps } from '../FormControl';
import { FormControl } from '../FormControl';

import type { InputProps } from './Input';
import { Input } from './Input';

export interface InputPhoneNumberWithControlProps
	extends
		Pick<FormControlProps, 'label' | 'outerStyle'>,
		Omit<InputProps, 'onChange' | 'value'> {
	name: string;
}

/**
 * Try to transform the given value into something that react-phone-number-input will display.
 *
 * react-phone-number-input does not display numbers that are not in E.164 format.
 */
function parsePhoneNumber(value: unknown): E164Number {
	if (typeof value !== 'string') return '';

	const parsedUS = libParse(value, 'US');
	if (parsedUS) {
		return parsedUS.number;
	}

	const parsedIntl = libParse(value);
	if (parsedIntl) {
		return parsedIntl.number;
	}

	return '+1' + value.replace(/[^0-9]+/u, '');
}

export function InputPhoneNumberWithControl(
	props: InputPhoneNumberWithControlProps
) {
	const { name, label, defaultValue, outerStyle, ...other } = props;

	const {
		field: { value, onChange, ...field },
		fieldState: { invalid },
		errorMessage,
	} = useField({ name, defaultValue });

	const cleanedValue = parsePhoneNumber(value);

	return (
		<FormControl
			outerStyle={outerStyle}
			label={label}
			error={invalid}
			helperText={errorMessage}
		>
			<ReactPhoneNumberInput
				{...field}
				onChange={(phoneNumber) => {
					/**
					 * Regular inputs will return empty string when input is empty,
					 * but react-phone-number-input will return undefined.
					 * This coercion makes the empty behavior unified across our inputs.
					 */
					if (phoneNumber === undefined) {
						onChange('');
					} else {
						onChange(phoneNumber);
					}
				}}
				value={cleanedValue}
				error={invalid}
				defaultCountry="US"
				inputComponent={Input}
				autoComplete="new-password"
				{...other}
			/>
		</FormControl>
	);
}
